/**
 * Klein module-level register tussen de auth-laag en de rest van de app.
 *
 * - tokenOphaler: geregistreerd door useRol; levert een vers ID-token voor elke API-call.
 * - sessieVerlopen-melder: geregistreerd door App; wordt afgevuurd zodra blijkt dat de sessie
 *   niet meer geldig is (MSAL kan niet stil verversen, of de server antwoordt met 401).
 *   App toont daarop één centrale overlay, i.p.v. een technische fout op acht losse plekken.
 */
let _tokenOphaler = null;      // async () => string|null, geregistreerd door useRol
let _sessieVerlopenCb = null;  // () => void, geregistreerd door App
let _dataConflictCb = null;    // (info) => void, geregistreerd door App

/**
 * Registreer een async token-ophaler (door useRol gezet met MSAL's acquireTokenSilent).
 * De ophaler geeft een VERS, zo nodig ververst ID-token terug, of null.
 */
export function setTokenOphaler(fn) {
  _tokenOphaler = typeof fn === 'function' ? fn : null;
}

/** Registreer de centrale "sessie verlopen"-melder. null = afmelden (bij unmount). */
export function meldSessieVerlopen(cb) {
  _sessieVerlopenCb = typeof cb === 'function' ? cb : null;
}

/** Vuur de sessie-verlopen-melding af. Niemand geregistreerd → stil. */
export function sessieVerlopen() {
  if (_sessieVerlopenCb) _sessieVerlopenCb();
}

/**
 * Registreer de "data-conflict"-melder (409 op PUT /api/data: een andere teamleider heeft
 * intussen geschreven). Zelfde vorm als meldSessieVerlopen — App toont er één centrale
 * overlay voor. null = afmelden (bij unmount).
 */
export function meldDataConflict(cb) {
  _dataConflictCb = typeof cb === 'function' ? cb : null;
}

/** Vuur de data-conflict-melding af met { doorNaam, tijdstip }. Niemand geregistreerd → stil. */
export function dataConflict(info) {
  if (_dataConflictCb) _dataConflictCb(info || {});
}

// MSAL gooit dit wanneer stil verversen niet meer kan (refresh-token weg of verlopen): de
// gebruiker MOET opnieuw inloggen. Zowel de klassenaam als de errorCode checken — welke van
// de twee je krijgt, hangt af van het MSAL-pad dat de fout produceert.
function isInteractieNodig(err) {
  return err?.name === 'InteractionRequiredAuthError'
    || err?.errorCode === 'interaction_required';
}

/** Haal een vers ID-token op via de geregistreerde ophaler, of null. */
export async function haalVersToken() {
  if (!_tokenOphaler) return null;
  try {
    return await _tokenOphaler();
  } catch (err) {
    // Stil verversen kan niet meer → centrale melding. Dit werd voorheen zwijgend geslikt:
    // er ging dan geen Authorization-header mee en de gebruiker kreeg een kale 401 te zien.
    if (isInteractieNodig(err)) sessieVerlopen();
    return null;
  }
}
